import React, { useEffect, useRef, useState } from "react";
import { ChromePicker } from "react-color";
import { ReactComponent as MicIcon } from "../../../../assets/svg/micIcon.svg";
import { ReactComponent as SpeakerIcon } from "../../../../assets/svg/speakerIcon.svg";
import { ReactComponent as KeypadIcon } from "../../../../assets/svg/keypadIcon.svg";
import { ReactComponent as PhoneIcon } from "../../../../assets/svg/phoneIcon.svg";
import { createPortal } from "react-dom";
import tinycolor from "tinycolor2";
import { ICallScreenSettings } from "src/types/models/apiModels";
import ButtonPro from "src/components/atoms/primary-button/primary-button";
import { defaultScreenSetting } from "src/models/default-screen-settings";
import CheckBox from "src/components/molecules/check-box/check-box";

interface ICallScreenProps {
	brandingImage: string;
	businessName: string;
	intent: string;
	currentScreenSettings: ICallScreenSettings;
	onChange?: (updatedSettings: ICallScreenSettings) => void;
}

const CallScreen: React.FC<ICallScreenProps> = ({
	brandingImage,
	businessName,
	intent,
	currentScreenSettings,
	onChange,
}) => {
	// Track initialization
	const isInitialized = useRef(false);

	// States
	const [backgroundColor, setBackgroundColor] = useState(
		currentScreenSettings.background.color
	);
	const [otherButtonForeground, setOtherButtonForeground] = useState(
		currentScreenSettings.otherButton.color.foreground
	); // Foreground color
	const [otherButtonBackground, setOtherButtonBackground] = useState(
		currentScreenSettings.otherButton.color.background
	); // Background color
	// Calculate lighter shade
	const lighterColor = tinycolor(backgroundColor).lighten(20).toString(); // Make it 20% lighter
	// Picker Visibility
	const [showBackgroundColorPicker, setShowBackgroundColorPicker] =
		useState(false);
	const [showMicButtonColorPicker, setShowMicButtonColorPicker] =
		useState(false);
	const [
		useBrandLogoColorsForBackground,
		setUseBrandLogoColorsForBackground,
	] = useState(currentScreenSettings.background.color === "-1");
	// Picker Positions
	const [backgroundPickerPosition, setBackgroundPickerPosition] = useState({
		x: 0,
		y: 0,
	});
	const [micPickerPosition, setMicPickerPosition] = useState({
		x: 0,
		y: 0,
	});

	const [tempBackgroundColor, setTempBackgroundColor] = useState(
		currentScreenSettings.background.color
	);

	// Picker Refs
	const backgroundPickerRef = useRef<HTMLDivElement | null>(null);
	const micPickerRef = useRef<HTMLDivElement | null>(null);

	// Close pickers when clicking outside
	const handleClickOutside = (event: MouseEvent) => {
		// Background picker
		if (
			backgroundPickerRef.current &&
			!backgroundPickerRef.current.contains(event.target as Node)
		) {
			setShowBackgroundColorPicker(false);
		}

		// Mic button picker
		if (
			micPickerRef.current &&
			!micPickerRef.current.contains(event.target as Node)
		) {
			setShowMicButtonColorPicker(false);
		}
	};

	// Attach and detach event listeners
	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const updateSettings = () => {
		if (isInitialized.current) {
			const updatedSettings: ICallScreenSettings = {
				otherButton: {
					color: {
						foreground: otherButtonForeground,
						background: otherButtonBackground,
					},
				},
				endCallButton: {
					color: {
						foreground:
							defaultScreenSetting.endCallButton.color.foreground,
						background:
							defaultScreenSetting.endCallButton.color.background,
					},
				},
				appLogo: {
					size: {
						width: defaultScreenSetting.appLogo.size.width,
						height: defaultScreenSetting.appLogo.size.height,
					},
				},
				background: {
					color: backgroundColor,
				},
				answerCallButton: {
					color: {
						foreground:
							defaultScreenSetting.answerCallButton.color
								.foreground,
						background:
							defaultScreenSetting.answerCallButton.color
								.background,
					},
				},
			};
			onChange && onChange(updatedSettings); // Trigger parent callback
		}
	};

	const resetToDefault = () => {
		setBackgroundColor(defaultScreenSetting.background.color);
		setOtherButtonForeground(
			defaultScreenSetting.otherButton.color.foreground
		);
		setOtherButtonBackground(
			defaultScreenSetting.otherButton.color.background
		);
	};

	useEffect(() => {
		// Avoid triggering onChange on initial render
		if (!isInitialized.current) {
			isInitialized.current = true;
			return;
		}
		updateSettings();
	}, [backgroundColor, otherButtonForeground, otherButtonBackground]);

	// Update Settings and Notify Parent

	// Handle background picker click
	const handleBackgroundClick = (event: React.MouseEvent<HTMLDivElement>) => {
		setBackgroundPickerPosition({ x: event.clientX, y: event.clientY });
		setShowBackgroundColorPicker(true);
	};

	// Handle mic button picker click
	const handleMicClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation(); // Prevent bubbling
		setMicPickerPosition({ x: event.clientX, y: event.clientY });
		setShowMicButtonColorPicker(true);
	};

	return (
		<>
			<div className="flex items-start justify-center gap-4">
				<div className="flex flex-col items-center justify-center gap-4">
					<div className="relative mx-auto border-gray-950 bg-black border-[14px] rounded-[2.5rem] h-[500px] w-[250px] drop-shadow-lg">
						{/* Phone Body */}
						<div className="h-[32px] w-[3px] bg-gray-950 absolute -start-[17px] top-[72px] rounded-s-lg"></div>
						<div className="h-[46px] w-[3px] bg-gray-950 absolute -start-[17px] top-[124px] rounded-s-lg"></div>
						<div className="h-[46px] w-[3px] bg-gray-950 absolute -start-[17px] top-[178px] rounded-s-lg"></div>
						<div className="h-[64px] w-[3px] bg-gray-950 absolute -end-[17px] top-[142px] rounded-e-lg"></div>
						<div
							style={{
								background: `linear-gradient(to top left, ${lighterColor}, ${backgroundColor === "-1" ? defaultScreenSetting.background.color : backgroundColor})`,
							}}
							className="rounded-[2rem] overflow-hidden w-[222px] h-[472px] relative cursor-pointer shadow-lg"
							onClick={handleBackgroundClick} // Open picker
						>
							{/* Content */}
							<div className="h-full w-full flex flex-col items-center justify-between pb-8 pt-4">
								<div className="flex flex-col items-center justify-center w-full">
									{/* Branding */}
									<div className="bg-black h-6 w-24 rounded-2xl"></div>
									<div className="flex flex-col items-center justify-center gap-8 w-full pt-6">
										<label className="font-Poppins font-semibold tracking-wider text-2xl text-white text-nowrap animate-marquee">
											{businessName}
										</label>
										<img
											src={brandingImage}
											alt=""
											className="w-32 h-32 rounded-full drop-shadow-lg"
										/>
										<label className="font-Poppins font-normal tracking-wider text-white text-center text-sm">
											{intent}
										</label>

										{/* Buttons */}
										<div className="flex flex-col items-center justify-evenly w-full gap-4">
											<div className="flex items-center justify-evenly w-full">
												{/* Mic Button */}
												<button
													style={{
														backgroundColor:
															otherButtonBackground,
														color: otherButtonForeground,
													}}
													className="p-3 rounded-full"
													onClick={handleMicClick}
												>
													<MicIcon className="w-6 h-6" />
												</button>

												<button
													style={{
														backgroundColor:
															otherButtonBackground,
														color: otherButtonForeground,
													}}
													className="p-3 rounded-full"
													onClick={handleMicClick}
												>
													<SpeakerIcon className="w-6 h-6" />
												</button>

												<button
													style={{
														backgroundColor:
															otherButtonBackground,
														color: otherButtonForeground,
													}}
													className="p-3 rounded-full"
													onClick={handleMicClick}
												>
													<KeypadIcon className="w-6 h-6" />
												</button>
											</div>
											{/* Phone End Button */}
											<button className="bg-red-600 p-3 rounded-full">
												<PhoneIcon className="w-6 h-6 text-white rotate-[135deg]" />
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<ButtonPro
						label={"Reset to default"}
						onClick={resetToDefault}
					></ButtonPro>
					{/* Background Picker */}
					{showBackgroundColorPicker &&
						createPortal(
							<div
								ref={backgroundPickerRef}
								style={{
									position: "absolute",
									top: `${backgroundPickerPosition.y}px`,
									left: `${backgroundPickerPosition.x}px`,
									zIndex: 1000,
								}}
								className="shadow-lg border-4 border-gray-400"
							>
								<div className="bg-white flex flex-col items-center justify-center gap-4">
									<span className="bg-sky-500 font-Poppins font-semibold text-md w-full text-center text-white p-2">
										Background Color
									</span>
									<div className="flex flex-col items-center justify-center gap-4 p-2">
										<div className="px-2">
											<CheckBox
												title="Use brand logo colors"
												checked={
													useBrandLogoColorsForBackground
												}
												onChange={function (
													value: boolean
												): void {
													setUseBrandLogoColorsForBackground(
														value
													);
													if (value) {
														setTempBackgroundColor(
															backgroundColor
														);
														setBackgroundColor(
															"-1"
														);
													} else {
														setBackgroundColor(
															tempBackgroundColor
														);
													}
												}}
											></CheckBox>
										</div>
										{!useBrandLogoColorsForBackground && (
											<ChromePicker
												color={backgroundColor}
												onChangeComplete={(color) => {
													console.log(`onchange`);
													setBackgroundColor(
														color.hex
													);
												}}
											/>
										)}
									</div>
								</div>
							</div>,
							document.body
						)}

					{/* Mic Button Picker */}
					{showMicButtonColorPicker &&
						createPortal(
							<div
								ref={micPickerRef}
								style={{
									position: "absolute",
									top: `${micPickerPosition.y}px`,
									left: `${micPickerPosition.x}px`,
									zIndex: 1000,
								}}
								className="shadow-lg"
							>
								<div className="bg-white flex items-center justify-center gap-4 border-4 border-gray-400">
									{/* Foreground */}
									<div className="bg-white flex flex-col items-center justify-center gap-4">
										<span className="font-Poppins font-semibold text-md w-full text-center p-2">
											Button foreground color
										</span>
										<div className="flex flex-col items-center justify-center gap-4 p-2">
											<ChromePicker
												color={otherButtonForeground}
												onChangeComplete={(color) => {
													setOtherButtonForeground(
														color.hex
													);
												}}
											/>
										</div>
									</div>
									{/* Background */}
									<div className="bg-white flex flex-col items-center justify-center gap-4">
										<span className="font-Poppins font-semibold text-md w-full text-center p-2">
											Button background color
										</span>
										<div className="flex flex-col items-center justify-center gap-4 p-2">
											<ChromePicker
												color={otherButtonBackground}
												onChangeComplete={(color) => {
													setOtherButtonBackground(
														color.hex
													);
												}}
											/>
										</div>
									</div>
								</div>
							</div>,
							document.body
						)}
				</div>
			</div>
		</>
	);
};

export default CallScreen;
